import { normalizeWord } from "./normalization";
import type {
  GameEventDefinition,
  LevelDefinition,
  RuntimeGameEventDefinition,
} from "./types";

export type SessionRandom = () => number;

export function createSeededRandom(seed: number): SessionRandom {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = value;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function challengeFormCandidates(
  words: readonly string[],
  ordinal: number,
): string[] {
  const expectsPhrase = ordinal % 2 === 0;
  const matching = words.filter(
    (word) => normalizeWord(word).includes(" ") === expectsPhrase,
  );
  return matching.length > 0 ? matching : [...words];
}

function isPhrase(word: string): boolean {
  return normalizeWord(word).includes(" ");
}

function phaseCandidates(words: readonly string[], ordinal: number): string[] {
  if (ordinal <= 6) return words.slice(0, Math.min(2, words.length));
  if (ordinal <= 14) {
    if (words.length <= 2) return [...words];
    return words.slice(1, -1);
  }
  return challengeFormCandidates(words, ordinal);
}

function pickUnused(
  candidates: readonly string[],
  allWords: readonly string[],
  used: Set<string>,
  rotation: number,
): string {
  const ordered = (source: readonly string[]): string[] =>
    source.map((_, index) => source[(index + rotation) % source.length] ?? "");
  const preferred = ordered(candidates).find(
    (word) => word.length > 0 && !used.has(normalizeWord(word)),
  );
  const fallback = ordered(allWords).find(
    (word) => word.length > 0 && !used.has(normalizeWord(word)),
  );
  return preferred ?? fallback ?? allWords[rotation % allWords.length] ?? "";
}

function toRuntime(
  definition: GameEventDefinition,
  word: string,
): RuntimeGameEventDefinition {
  return {
    id: definition.id,
    type: definition.type,
    basePoints: definition.basePoints,
    response: definition.response,
    reward: definition.reward,
    word,
  };
}

export function buildSessionSequence(
  level: LevelDefinition,
  attemptIndex: number,
): RuntimeGameEventDefinition[] {
  const definitions = new Map(level.events.map((event) => [event.id, event]));
  const usedByConcept = new Map<string, Set<string>>();
  const occurrenceByConcept = new Map<string, number>();

  return level.sequence.map((eventId, index) => {
    const definition = definitions.get(eventId);
    if (!definition) throw new Error(`Unknown event ${eventId}`);
    const used = usedByConcept.get(eventId) ?? new Set<string>();
    const occurrence = occurrenceByConcept.get(eventId) ?? 0;
    const initialCandidates = phaseCandidates(definition.words, index + 1);
    const futureChallengeOrdinals = level.sequence
      .map((futureId, futureIndex) => ({
        futureId,
        ordinal: futureIndex + 1,
      }))
      .filter(
        ({ futureId, ordinal }) =>
          futureId === eventId && ordinal > index + 1 && ordinal >= 15,
      )
      .map(({ ordinal }) => ordinal);
    const neededSingles = futureChallengeOrdinals.filter(
      (ordinal) => ordinal % 2 === 1,
    ).length;
    const neededPhrases = futureChallengeOrdinals.length - neededSingles;
    const unused = definition.words.filter(
      (candidate) => !used.has(normalizeWord(candidate)),
    );
    const preservesChallengeReserve = (candidate: string): boolean => {
      if (used.has(normalizeWord(candidate))) return false;
      const phrase = isPhrase(candidate);
      const remainingOfForm = unused.filter(
        (word) =>
          isPhrase(word) === phrase &&
          normalizeWord(word) !== normalizeWord(candidate),
      ).length;
      return remainingOfForm >= (phrase ? neededPhrases : neededSingles);
    };
    const safePreferred = initialCandidates.filter(preservesChallengeReserve);
    const safeFallback = definition.words.filter(preservesChallengeReserve);
    const candidates =
      safePreferred.length > 0
        ? safePreferred
        : safeFallback.length > 0
          ? safeFallback
          : initialCandidates;
    const word = pickUnused(
      candidates,
      definition.words,
      used,
      attemptIndex + occurrence,
    );
    used.add(normalizeWord(word));
    usedByConcept.set(eventId, used);
    occurrenceByConcept.set(eventId, occurrence + 1);
    return toRuntime(definition, word);
  });
}

export interface RecoveryScheduleResult {
  sequence: RuntimeGameEventDefinition[];
  replacedIndexes: number[];
}

export function scheduleAdaptiveRecoveries(
  sequence: readonly RuntimeGameEventDefinition[],
  nextIndex: number,
  recovery: GameEventDefinition,
  attemptIndex: number,
  random: SessionRandom,
): RecoveryScheduleResult {
  const planned = [...sequence];
  if (nextIndex >= planned.length)
    return { sequence: planned, replacedIndexes: [] };

  const wanted = random() < 0.5 ? 1 : 2;
  const replacedIndexes = [nextIndex];
  if (wanted === 2) {
    const later = Array.from(
      { length: Math.max(0, planned.length - (nextIndex + 2)) },
      (_, offset) => nextIndex + 2 + offset,
    );
    if (later.length > 0) {
      const chosen = later[Math.floor(random() * later.length)];
      if (chosen !== undefined) replacedIndexes.push(chosen);
    }
  }

  const shortVariants = recovery.words.slice(
    0,
    Math.min(2, recovery.words.length),
  );
  const used = new Set<string>();
  replacedIndexes.forEach((index, recoveryIndex) => {
    const ordinal = index + 1;
    const preferred =
      ordinal >= 15
        ? challengeFormCandidates(recovery.words, ordinal)
        : shortVariants;
    const word = pickUnused(
      preferred,
      recovery.words,
      used,
      attemptIndex + recoveryIndex,
    );
    used.add(normalizeWord(word));
    planned[index] = toRuntime(recovery, word);
  });

  return { sequence: planned, replacedIndexes };
}
